import { StackProps, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { COLOR_ACCENT, COLOR_BG_SECONDARY, COLOR_PRIMARY } from 'shared';
import CopyField from 'shared/ui/copy-field';
import { TokenFormat } from 'shared/ui/token-format';

import { WarningBox } from './warning-box';

interface EOAHintProps {
  packageName: string;
  tokenDecimals?: number;
  tokenAmount: number | string;
  tokenSymbol: string;
  isConfirmed?: boolean;
}

interface EOAInfoProps extends EOAHintProps {
  paymentAddress?: string;
  showEoaHint?: boolean;
  network: string;
}

export const EOAHint = ({ packageName, tokenAmount, tokenDecimals, tokenSymbol, isConfirmed }: EOAHintProps) => {
  const amountElement = (
    <TokenFormat
      amount={tokenAmount.toString()}
      maxDecimalDigits={8}
      decimals={tokenDecimals}
      symbol={tokenSymbol}
      className="primary"
    />
  );

  if (isConfirmed)
    return (
      <Typography variant="body2" className="secondary" mb={1}>
        To purchase{' '}
        <Typography component="span" className="accent">
          {packageName} package
        </Typography>{' '}
        <Typography component="span" className="primary">
          transfer {amountElement}
        </Typography>{' '}
        to
      </Typography>
    );

  return (
    <Typography variant="body2" className="secondary" mb={1}>
      You will spend approx. {amountElement} to purchase{' '}
      <Typography component="span" className="accent">
        {packageName} package
      </Typography>
    </Typography>
  );
};

export const EOAInfo = ({
  packageName,
  tokenSymbol,
  network,
  paymentAddress,
  showEoaHint = true,
  tokenAmount,
  isConfirmed = false,
  tokenDecimals,
}: EOAInfoProps) => {
  return (
    <Stack width="100%" bgcolor={COLOR_BG_SECONDARY} alignItems="flex-start" borderRadius={3} p={1.5} mb={2}>
      {showEoaHint && (
        <EOAHint
          packageName={packageName}
          tokenDecimals={tokenDecimals}
          tokenAmount={tokenAmount}
          tokenSymbol={tokenSymbol}
          isConfirmed={isConfirmed}
        />
      )}

      {paymentAddress && isConfirmed && (
        <CopyField
          displayValue={paymentAddress}
          copyValue={paymentAddress}
          successCopyMessage="Address copied"
          mb={1.2}
          btnSx={{
            color: COLOR_ACCENT,
            '&:hover': { color: COLOR_PRIMARY },
          }}
        />
      )}

      <Typography variant="body2" className="secondary">
        Make sure to choose{' '}
        <Typography component="span" textTransform="capitalize" className="primary">
          {network}{' '}
        </Typography>
        in your wallet when making transfer
      </Typography>
    </Stack>
  );
};

export const EOAWarning = (props: Omit<StackProps, 'children'>) => {
  return (
    <WarningBox {...props}>
      <Typography variant="body2" className="secondary">
        <Typography component="span" variant="body2" className="primary">
          Warning:{' '}
        </Typography>
        the order and amount is only valid for 4 hours, if no payment is received during the specified time, your order
        will be canceled and you will need to start again. Transfer crypto under your own responsibility, overpaid coins
        won`t be refunded.
      </Typography>
    </WarningBox>
  );
};
